import Box from '@mui/material/Box';
import type { ButtonProps } from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Skeleton from '@mui/material/Skeleton';
import type { StackProps } from '@mui/material/Stack';
import Stack from '@mui/material/Stack';
import { styled, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { ReactElement } from 'react';
import React from 'react';

import type { TestProps } from '../../types';

export type SectionProps = Readonly<{
  title: string;
  titleItems?: React.ReactNode;
  titleChip?: React.ReactNode;
  actions?: ReactElement<ButtonProps>[];
  variant?: 'section' | 'subsection';
  slotProps?: {
    content?: StackProps;
  };
}> &
  TestProps &
  StackProps;

const SectionDivider = styled(Divider)(() => ({
  borderBottomWidth: 2,
  borderColor: '#ABABAE', // temp until theme.palette.neutral[400] is added to palette
}));

export const Section: React.FC<SectionProps> = ({
  title,
  titleItems,
  titleChip,
  actions,
  children,
  variant = 'section',
  slotProps,
  ...rest
}) => {
  if (variant === 'subsection') {
    return (
      <Stack {...rest}>
        <Divider />
        <Stack direction="row" alignItems="center" height={t => t.spacing(6)} gap={1}>
          <Typography variant="highlight1">{title}</Typography>
          <Box flexGrow={1}>{titleItems}</Box>
          {actions}
        </Stack>
        <Stack {...slotProps?.content}>{children}</Stack>
      </Stack>
    );
  }

  return (
    <Stack {...rest}>
      <SectionDivider />
      <Stack direction="row" alignItems="center" height={t => t.spacing(6)} p={1} gap={1}>
        <Box display="flex" alignItems="center">
          <Typography variant="h3">{title}</Typography>
          {titleChip && (
            <Box marginLeft={1} display="flex" alignItems="center">
              {titleChip}
            </Box>
          )}
        </Box>
        <Box flexGrow={1}>{titleItems}</Box>
        {actions}
      </Stack>
      <Stack p={1} {...slotProps?.content}>
        {children}
      </Stack>
    </Stack>
  );
};

export type SectionSkeletonProps = TestProps & StackProps;

export const SectionSkeleton: React.FC<SectionSkeletonProps> = ({ children, ...rest }) => {
  const theme = useTheme();

  return (
    <Stack {...rest} role="progressbar">
      <SectionDivider />
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        height={t => t.spacing(6)}
        p={1}
      >
        <Skeleton width={theme.spacing(32)} />
        <Skeleton width={theme.spacing(8)} />
      </Stack>
      <Stack px={1}>{children}</Stack>
    </Stack>
  );
};

export const SectionContentSkeleton: React.FC<SectionSkeletonProps> = props => {
  const theme = useTheme();

  return (
    <Stack {...props} gap={2} role="progressbar">
      <Skeleton variant="rounded" height={theme.spacing(2)} />
      <Skeleton variant="rounded" height={theme.spacing(2)} />
      <Skeleton variant="rounded" height={theme.spacing(2)} />
    </Stack>
  );
};
